import React, { CSSProperties, FC, ReactNode } from 'react';
import styled from 'styled-components';
import { SyntaxHighlighterProps } from 'react-syntax-highlighter';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/prism-light';
import { AssetUrl } from '@cld/asset-url';
import { AssetUrlProps } from '../../types';
import { Syntax } from '../../Syntax';
import codeStyle from './Code.style';
import { SUPPORTED_LANGUAGES } from './supportedLanguages';

SUPPORTED_LANGUAGES.forEach(({ name, value }) => SyntaxHighlighter.registerLanguage(name, value));

const Root = styled.div`
  ${codeStyle};
  height: 100%;
`;

export const StyledLink = styled.a`
  font-weight: normal;
`;

export interface CodeProps extends SyntaxHighlighterProps {
  /** The language to highlight, one of Prism styles */
  syntax?: Syntax;
  showLineNumbers?: boolean;
  lineNumberContainerStyle?: CSSProperties;
  parserPayload?: AssetUrlProps;
}

// Hack used to disable inline style in Prism flavour of react-syntax-highlighter
const disablesInlineStyle = {};

const baseLineNumberContainerStyle: CSSProperties = {
  float: 'left',
  paddingRight: '10px',
  display: 'inline-block',
};

const syntaxMap: Record<string, Syntax> = {
  url: 'http',
  flutter: 'dart',
  react_native: 'jsx',
};

const getSyntax = (syntax: Syntax) => syntaxMap[syntax] || syntax;

const getPrismHighlightedCode = (syntax: Syntax, children: ReactNode, showLineNumbers?: boolean, lineNumberContainerStyle?: CSSProperties) => (
  <SyntaxHighlighter
    showLineNumbers={showLineNumbers}
    lineNumberContainerStyle={{ ...baseLineNumberContainerStyle, ...lineNumberContainerStyle }}
    language={syntax}
    useInlineStyles={false}
    style={disablesInlineStyle}
    codeTagProps={{ style: disablesInlineStyle }}
    data-test-specifier="code-snippets-code"
  >
    {children as string}
  </SyntaxHighlighter>
);

const getParsedUrlWithFallback = (url: string, parsedUrl?: AssetUrlProps): AssetUrlProps => {
  if (!parsedUrl || Object.values(parsedUrl).every((part) => !part)) {
    // Fallback case when parsing of URL failed (all values are empty).
    // It will be displayed, but without different colors for its parts.
    return { prefix: url };
  }
  return parsedUrl;
};

const getUrlCustomHighlightedCode = (parsedUrl: AssetUrlProps, url: string) => (
  <pre data-test-specifier="code-snippets-code">
    <code>
      <StyledLink href={url} target="_blank" rel="noopener noreferrer">
        <AssetUrl.FromObject {...getParsedUrlWithFallback(url, parsedUrl)} />
      </StyledLink>
    </code>
  </pre>
);

/**
 * A view that renders code of a specified language and colorful syntax
 * if parsedUrl (cld syntax highlighting) not provided, use Prism highlight
 */
export const Code: FC<React.PropsWithChildren<CodeProps>> = ({ showLineNumbers, syntax, children, lineNumberContainerStyle, parsedUrl, ...rest }) => {
  return (
    <Root {...rest}>
      {syntax &&
        (syntax === 'url' && parsedUrl
          ? getUrlCustomHighlightedCode(parsedUrl, String(children))
          : getPrismHighlightedCode(getSyntax(syntax), children, showLineNumbers, lineNumberContainerStyle))}
    </Root>
  );
};
